import { SupplierPartConfirmationResponseDto } from "../types";
import { applyAdjustment, SupplierPart } from "./partsCalculationUtils";

/**
 * Build the composite key used to look up a part-confirmation quantity
 * for a specific supplier + root part family.
 */
export const getSupplierPartConfirmationKey = (
  supplierId: string,
  rootPartNumber: string,
): string => `${supplierId}::${rootPartNumber}`;

/**
 * Apply part-confirmation quantities to supplier parts.
 *
 * This is the LAST quantity adjustment in the pipeline:
 *   Typesense parts → offline merge → delta → THIS → metrics
 *
 * For each part, the adjusted quantity is:
 *   Math.max(0, familyQuantity - confirmationQuantity)
 *
 * Parts without a matching confirmation entry are returned unchanged.
 */
export const applySupplierPartConfirmationToParts = <T extends SupplierPart>({
  supplierId,
  parts,
  supplierPartConfirmationQuantityMap,
}: {
  supplierId: string;
  parts: T[];
  supplierPartConfirmationQuantityMap?: Map<string, number>;
}): T[] => {
  if (!supplierPartConfirmationQuantityMap?.size || !supplierId) return parts;

  return parts.map((part) => {
    const rootPartNumber = part.rootPartNumber;
    if (!rootPartNumber) return part;

    const supplierPartConfirmationQuantity =
      supplierPartConfirmationQuantityMap.get(
        getSupplierPartConfirmationKey(supplierId, rootPartNumber),
      ) ?? 0;

    if (supplierPartConfirmationQuantity <= 0) return part;

    const familyQuantity = Number(part.familyQuantity) || 0;
    const adjustedFamilyQuantity = Math.max(
      0,
      familyQuantity - supplierPartConfirmationQuantity,
    );

    return applyAdjustment(
      part,
      { label: "Part Confirmation", value: supplierPartConfirmationQuantity },
      adjustedFamilyQuantity,
    );
  });
};

/**
 * Build a lookup map from the part-confirmation API response.
 *
 * Key:   "supplierId::rootPartNumber"
 * Value: quantity (number)
 *
 * Returns an empty map if response is undefined or has no results.
 */
export const buildSupplierPartConfirmationQuantityMap = (
  response: SupplierPartConfirmationResponseDto | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!response?.results) return map;

  for (const confirmation of response.results) {
    if (!confirmation.supplierId || !confirmation.rootPartNumber) continue;
    map.set(
      getSupplierPartConfirmationKey(
        confirmation.supplierId,
        confirmation.rootPartNumber,
      ),
      Number(confirmation.quantity) || 0,
    );
  }
  return map;
};
